import { memo, useCallback, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import {
  apiSetupPasswordLogin,
  apiSendVerificationCode,
  apiVerifyCode,
  apiSetPassword,
} from "lib/api";
import * as jose from "jose";
import { EmailStep } from "components/SetPasswordFlow/EmailStep";
import { VerificationStep } from "components/SetPasswordFlow/VerificationStep";
import { PasswordStep } from "components/SetPasswordFlow/PasswordStep";
import { ExitConfirmationModal } from "components/SetPasswordFlow/ExitConfirmationModal";

type Step =
  | "email"
  | "confirm"
  | "newEmail"
  | "verify"
  | "password"
  | "success";

type EmailFormValue = {
  email: string;
};

type PasswordFormValue = {
  password: string;
  confirmPassword: string;
};

export const SetPassword = memo(function SetPassword() {
  const [originalEmail, setOriginalEmail] = useState("");
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [step, setStep] = useState<Step>("email");
  const [code, setCode] = useState(["", "", "", "", ""]);
  const [processing, setProcessing] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showPasswordSuccessModal, setShowPasswordSuccessModal] =
    useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [emailLinkError, setEmailLinkError] = useState("");

  const {
    register,
    handleSubmit: handleEmailSubmit,
    formState: { isValid: isEmailValid, errors: emailErrors },
    setError: setEmailError,
  } = useForm<EmailFormValue>({
    defaultValues: {
      email: "",
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { isValid: isPasswordValid, errors: passwordErrors },
    setError: setPasswordError,
  } = useForm<PasswordFormValue>({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const onEmailSubmit = useCallback<SubmitHandler<EmailFormValue>>(
    async ({ email }) => {
      setProcessing(true);
      try {
        // Use setup-password-login endpoint (no password required)
        const token = await apiSetupPasswordLogin(email);
        const payload = jose.decodeJwt(token);

        if (payload.registered) {
          setOriginalEmail(email);
          setVerifiedEmail(email); // Default to original email
          setStep("confirm");
        } else {
          setEmailError("email", {
            type: "custom",
            message:
              "No account found with this email. Please check for typos or create a new ContributorID.",
          });
        }
      } catch (err: any) {
        setEmailError("email", {
          type: "custom",
          message: "Unable to verify email. Please try again later.",
        });
      } finally {
        setProcessing(false);
      }
    },
    [setEmailError]
  );

  const handleContinueWithOriginal = useCallback(async () => {
    setProcessing(true);
    setEmailLinkError("");
    try {
      await apiSendVerificationCode(originalEmail, true); // true = is original email
      setStep("verify");
    } catch (err: any) {
      if (err?.code === "email_is_used") {
        setEmailLinkError(
          "This email is already associated with another account."
        );
      } else {
        setEmailLinkError(
          "Failed to send verification code. Please try again."
        );
      }
    } finally {
      setProcessing(false);
    }
  }, [originalEmail]);

  const handleLinkNewEmail = useCallback(() => {
    setEmailLinkError("");
    setStep("newEmail");
  }, []);

  const handleConfirmNewEmail = useCallback(async () => {
    setProcessing(true);
    setEmailLinkError("");
    try {
      await apiSendVerificationCode(newEmail, false); // false = new email, check for duplicates
      setVerifiedEmail(newEmail);
      setStep("verify");
    } catch (err: any) {
      if (err?.code === "email_is_used") {
        setEmailLinkError(
          "This email is already associated with another account."
        );
      } else {
        setEmailLinkError(
          "Failed to send verification code. Please try again."
        );
      }
    } finally {
      setProcessing(false);
    }
  }, [newEmail]);

  const handleGoBackFromNewEmail = useCallback(() => {
    setStep("confirm");
  }, []);

  const handleCodeChange = useCallback(
    (index: number, value: string) => {
      if (value.length <= 1 && /^[0-9]*$/.test(value)) {
        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);

        // Auto-focus next input
        if (value && index < 4) {
          const nextInput = document.getElementById(`code-${index + 1}`);
          nextInput?.focus();
        }
      }
    },
    [code]
  );

  const handleCodeKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace" && !code[index] && index > 0) {
        const prevInput = document.getElementById(`code-${index - 1}`);
        prevInput?.focus();
      }
    },
    [code]
  );

  const handleVerifyCode = useCallback(async () => {
    const codeString = code.join("");
    if (codeString.length !== 5) {
      return;
    }

    setProcessing(true);
    try {
      await apiVerifyCode(verifiedEmail, codeString);
      setShowSuccessModal(true);
    } catch (err: any) {
      setCode(["", "", "", "", ""]);
    } finally {
      setProcessing(false);
    }
  }, [code, verifiedEmail]);

  const handleSuccessModalClose = useCallback(() => {
    setShowSuccessModal(false);
    setStep("password");
  }, []);

  const handleResendCode = useCallback(async () => {
    setProcessing(true);
    try {
      await apiSendVerificationCode(verifiedEmail);
      setCode(["", "", "", "", ""]);
    } catch (err: any) {
      // Silently fail - user can try again
    } finally {
      setProcessing(false);
    }
  }, [verifiedEmail]);

  const handleChangeEmail = useCallback(() => {
    setStep("confirm");
  }, []);

  const onPasswordSubmit = useCallback<SubmitHandler<PasswordFormValue>>(
    async ({ password, confirmPassword }) => {
      // Validate password requirements
      if (
        password.length < 8 ||
        !/[A-Z]/.test(password) ||
        !/[a-z]/.test(password) ||
        !/[0-9]/.test(password)
      ) {
        setPasswordError("password", {
          type: "custom",
          message: "Password does not meet requirements",
        });
        return;
      }

      if (password !== confirmPassword) {
        setPasswordError("confirmPassword", {
          type: "custom",
          message: "Passwords do not match",
        });
        return;
      }

      setProcessing(true);
      try {
        await apiSetPassword(verifiedEmail, password, originalEmail);
        setShowPasswordSuccessModal(true);
      } catch (err: any) {
        setPasswordError("password", {
          type: "custom",
          message: "Failed to set password. Please try again.",
        });
      } finally {
        setProcessing(false);
      }
    },
    [setPasswordError, verifiedEmail, originalEmail]
  );

  // Email Steps (Steps 1, 2, 2b)
  if (step === "email" || step === "confirm" || step === "newEmail") {
    return (
      <EmailStep
        step={step}
        originalEmail={originalEmail}
        newEmail={newEmail}
        processing={processing}
        isEmailValid={isEmailValid}
        emailErrors={emailErrors}
        emailLinkError={emailLinkError}
        register={register}
        onEmailSubmit={handleEmailSubmit(onEmailSubmit)}
        onContinueWithOriginal={handleContinueWithOriginal}
        onLinkNewEmail={handleLinkNewEmail}
        onConfirmNewEmail={handleConfirmNewEmail}
        onGoBackFromNewEmail={handleGoBackFromNewEmail}
        onGoBackToEmailInput={() => setStep("email")}
        setNewEmail={setNewEmail}
      />
    );
  }

  // Verification Step (Step 3)
  if (step === "verify") {
    return (
      <VerificationStep
        verifiedEmail={verifiedEmail}
        code={code}
        processing={processing}
        showSuccessModal={showSuccessModal}
        onCodeChange={handleCodeChange}
        onCodeKeyDown={handleCodeKeyDown}
        onVerifyCode={handleVerifyCode}
        onResendCode={handleResendCode}
        onChangeEmail={handleChangeEmail}
        onSuccessModalClose={handleSuccessModalClose}
      />
    );
  }

  // Password Step (Step 4)
  if (step === "password") {
    return (
      <>
        <PasswordStep
          processing={processing}
          isPasswordValid={isPasswordValid}
          passwordErrors={passwordErrors}
          showPasswordSuccessModal={showPasswordSuccessModal}
          registerPassword={registerPassword}
          onPasswordSubmit={handlePasswordSubmit(onPasswordSubmit)}
          onBackClick={() => setShowExitConfirmation(true)}
          originalEmail={originalEmail}
          verifiedEmail={verifiedEmail}
        />
        <ExitConfirmationModal
          show={showExitConfirmation}
          onClose={() => setShowExitConfirmation(false)}
        />
      </>
    );
  }

  return null;
});
